import { Component, Inject, Input, OnInit } from '@angular/core';
import { IdentityUserService } from 'src/app/services/identityUser.service';
import { User } from 'src/app/models/user';
import { FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { DOCUMENT, Location } from '@angular/common';
import { LoggingService } from 'src/app/services/logging.service';
import { OnlyLettersAndNumbersValidator } from 'src/app/directives/only-letters-and-numbers-validator.directive';
import { MarkFormGroupTouchedAndDirty } from 'src/app/directives/mark-form-group-as-touched-dirty.function';
import { VerifyAsEmailValidator } from 'src/app/directives/verify-as-email-validator.directive';
import { CannotContainOnlySpaceValidator } from 'src/app/directives/cannot-contain-only-space-validator.directive';
import { ActivatedRoute } from '@angular/router';
import { SettingsService } from '../settings.service';
import { PKIUrls } from 'src/app/constants/pkiurls.constants';
import { VerifyAsMobileValidator } from 'src/app/directives/verify-as-mobile-validator.directive';

@Component({
  selector: 'app-editable-user-profile',
  templateUrl: './editable-user-profile.component.html'
})
export class EditableUserProfileComponent implements OnInit {
  public UsersUrl: string = PKIUrls.UsersUrl;
  @Input() userGuid!: string;



  selectedUserId: string = "";
  user: User = new User;
  translations: any = <any>{};
  userLanguage: string = 'Language.Dutch';
  emailEditable: boolean = false;

  form!: FormGroup;
  firstNameControl!: FormControl;
  prefixControl!: FormControl;
  lastNameControl!: FormControl;
  emailControl!: FormControl;
  organisationControl!: FormControl;
  languageControl!: FormControl;
  phoneNumberControl!: FormControl;

  awaitingRequest = false;
  constructor(@Inject(DOCUMENT) private docElements: Document, private formBuilder: UntypedFormBuilder, private userService: IdentityUserService,
    private toastService: ToastService, private translateService: TranslateService, private location: Location, private loggingService: LoggingService, private settingsService: SettingsService, private activatedRoute: ActivatedRoute) {
    this.getTranslations();
    translateService.onLangChange.subscribe(() => {
      this.getTranslations();
    });
    this.setupForm();
  }

  ngOnInit() {
    this.getUserDetails(this.userGuid);
    this.emailEditable = this.userGuid != this.settingsService.currentUserId;
  }


  private setupForm() {
    this.firstNameControl = new FormControl('', [Validators.required, OnlyLettersAndNumbersValidator(), CannotContainOnlySpaceValidator()]);
    this.prefixControl = new FormControl('', [OnlyLettersAndNumbersValidator()]);
    this.lastNameControl = new FormControl('', [Validators.required, OnlyLettersAndNumbersValidator(), CannotContainOnlySpaceValidator()]);
    this.emailControl = new FormControl({ value: '', disabled: !this.emailEditable }, [Validators.required, VerifyAsEmailValidator()]);
    this.phoneNumberControl = new FormControl('', [Validators.required, CannotContainOnlySpaceValidator(), Validators.maxLength(15), VerifyAsMobileValidator()]);
    this.organisationControl = new FormControl('', []);
    this.languageControl = new FormControl('', []);

    this.form = this.formBuilder.group(
      {
        firstname: this.firstNameControl,
        prefix: this.prefixControl,
        lastname: this.lastNameControl,
        email: this.emailControl,
        phoneNumber: this.phoneNumberControl,
        organisation: this.organisationControl,
        language: this.languageControl,
      });

  }

  getTranslations() {
    this.translateService.get([
      'Toasts.UpdateUserError',
      'Toasts.InvalidFormFields',
      'Toasts.Error',
    ]).subscribe(translation => {
      this.translations.toastsUpdateUserError = translation['Toasts.UpdateUserError'];
      this.translations.toastsInvalidFormFields = translation['Toasts.InvalidFormFields'];
      this.translations.error = translation['Toasts.Error'];
    });
  }

  getUserDetails(forUserGuid: string) {
    this.userService.getUser(forUserGuid).subscribe({
      error: () => {
        //TODO
      },
      next: (user: User) => {
        this.user = user;
        this.firstNameControl.setValue(this.user.firstname);
        this.prefixControl.setValue(this.user.prefix);
        this.lastNameControl.setValue(this.user.lastname);
        this.emailControl.setValue(this.user.email);
        this.phoneNumberControl.setValue(this.user.phoneNumber);
        this.organisationControl.setValue(this.user.organisation);

        this.setUserLanguage(this.user.language);
      }
    });
  }

  setUserLanguageWithEvent(event: Event, language: string) {
    event.preventDefault();
    this.setUserLanguage(language);
  }

  private setUserLanguage(language: string) {
    if (language && language.toLowerCase() === 'en') {
      this.user.language = 'en';
      this.languageControl.setValue('en');
      this.userLanguage = 'Language.English';
    } else {
      this.user.language = 'nl';
      this.languageControl.setValue('nl');
      this.userLanguage = 'Language.Dutch';
    }
  }

  async SaveUser() {
    if (this.form.invalid) {
      MarkFormGroupTouchedAndDirty(this.form);
      this.toastService.showInvalidFields();
      return;
    }
    if (this.awaitingRequest) {
      return;
    }
    this.awaitingRequest = true;
    // GetRawValue, because form.value doesn't include e-mail, when disabled.
    this.user = this.form.getRawValue();
    this.UpdateUser(this.user).catch((error) => this.loggingService.logException(error));
  }

  async UpdateUser(user: User) {
    (await this.userService.updateUser(this.userGuid, user)).subscribe({
      error: (error: HttpErrorResponse) => {
        this.toastService.showError(this.translations.error, this.translations.toastsUpdateUserError);
        this.awaitingRequest = false;
      },
      next: (value: any) => {
        if (this.user.language !== localStorage.getItem('language')?.toLocaleLowerCase()) {
          localStorage.setItem("language", this.user.language.toLowerCase());
          this.translateService.use(this.user.language.toLowerCase());
          this.docElements.documentElement.lang = this.user.language.toLowerCase();
        }
        this.toastService.showSaved();
        this.awaitingRequest = false;
      }
    });
  }

}
